/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useLockBodyScroll } from "react-use";
import { Button, Sidebar, Spacer, Text, theme } from "@nordcloud/gnui";
import { EstateV2Filter } from "~/generated/graphql";
import { FiltersFooter, InputLoader } from "~/components";
import { useQueryState } from "~/hooks";
import { getTypedKeys, isEmptyObj, noop } from "~/tools";
import { convertTags } from "../helpers";
import {
  SelectableListWithSearch,
  DiscoveryTimeFilter,
  UnallocatedEstateFilter,
  FilterWrapper,
  LifeSpanInput,
  CostInput,
  AppEnvAccordion,
} from "./Filters";
import { EstateFilterHeader } from "./Filters/EstateFilterHeader";
import { ProvidersFilter } from "./Filters/ProvidersFilter";
import { useDataOptions } from "./hooks";
import { EstateMultiTagsInput } from "./MultiTagsInput";
import { FiltersProp } from "./types";

type Props = {
  isSidebarOpen: boolean;
  filters: FiltersProp;
  toggleIsSidebarOpen: (nextValue?: boolean) => void;
  updateFilters: (filter: FiltersProp) => void;
  handleApply: (filter: EstateV2Filter) => void;
  toggleFilters: (openFilters: boolean) => void;
  clearFilters: () => void;
};

export function FiltersSidebar({
  isSidebarOpen,
  filters,
  toggleIsSidebarOpen,
  updateFilters,
  handleApply,
  toggleFilters,
  clearFilters,
}: Props) {
  const { updateQueryState, removeFields } = useQueryState();

  const hasEmptyFilters = isEmptyObj(filters);

  const {
    accountsOptions,
    regionOptions,
    resourceTypes,
    categories,
    externalIntegrations,
    cloudWastePolicies,
    loadingCategories,
    loadingResourceTypes,
    loadingAccounts,
  } = useDataOptions(filters);

  const selectedAccounts = (filters?.account ?? []).map((account) =>
    account.toString()
  );

  const commit = () => {
    updateQueryState({ ...filters, page: 0 });
    toggleIsSidebarOpen(false);
    handleApply({
      ...filters,
      metadata: convertTags(filters.metadata),
      tags: convertTags(filters.tags),
    });
  };

  const reset = () => {
    removeFields([...getTypedKeys(filters), "page"]);
    toggleIsSidebarOpen(false);
    handleApply({});
    clearFilters();
  };

  useLockBodyScroll(isSidebarOpen);

  return (
    <Sidebar
      title="Filters"
      isOpen={isSidebarOpen}
      width="37rem"
      onClick={() => toggleFilters(false)}
    >
      <Text weight="medium">Costs and Savings</Text>
      <FilterWrapper
        name="Monthly Cost"
        onClear={() =>
          updateFilters({
            costFrom: undefined,
            costTo: undefined,
          })
        }
      >
        <EstateFilterHeader onSetSearch={noop} />
        <CostInput filters={filters} updateFilters={updateFilters} size="m" />
      </FilterWrapper>
      <FilterWrapper
        name="Estate Records with Savings Suggestions"
        selectedOptions={filters.wastePolicy ?? []}
        onClear={() => updateFilters({ wastePolicy: undefined })}
      >
        <SelectableListWithSearch
          selectedOptions={filters?.wastePolicy ?? []}
          selectableItems={cloudWastePolicies}
          onMultiSelect={(wastePolicy) => updateFilters({ wastePolicy })}
        />
      </FilterWrapper>
      <Text weight="medium">Cost Allocation</Text>
      <FilterWrapper
        name="Applications and Environments"
        selectedOptions={filters.application}
        onClear={() =>
          updateFilters({
            application: undefined,
            environment: undefined,
          })
        }
      >
        <AppEnvAccordion
          selectedApplications={filters.application ?? []}
          selectedEnvironments={filters.environment ?? []}
          onMultiSelect={updateFilters}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Unallocated Estate Records"
        onClear={() =>
          updateFilters({
            assignmentUpdatedFrom: undefined,
            assignmentUpdatedTo: undefined,
          })
        }
      >
        <UnallocatedEstateFilter
          filters={filters}
          updateFilters={updateFilters}
        />
      </FilterWrapper>
      <Text weight="medium" marginTop={theme.spacing.spacing04}>
        Filters by resource attributes
      </Text>
      <FilterWrapper
        name="Cloud Providers"
        selectedOptions={filters.provider ?? []}
        onClear={() => updateFilters({ provider: undefined })}
      >
        <ProvidersFilter
          selectedProviders={filters.provider ?? []}
          onProviderSelect={(provider) => updateFilters({ provider })}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Region"
        selectedOptions={filters.region ?? []}
        onClear={() => updateFilters({ region: undefined })}
      >
        <SelectableListWithSearch
          selectedOptions={filters?.region ?? []}
          selectableItems={regionOptions}
          onMultiSelect={(region) => updateFilters({ region })}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Cloud Accounts"
        selectedOptions={selectedAccounts}
        onClear={() => updateFilters({ account: undefined })}
      >
        <InputLoader loading={loadingAccounts}>
          <SelectableListWithSearch
            selectedOptions={filters?.account ?? []}
            selectableItems={accountsOptions.map((account) => ({
              value: account.value,
              label: account.label,
            }))}
            onMultiSelect={(account) => updateFilters({ account })}
          />
        </InputLoader>
      </FilterWrapper>
      <FilterWrapper
        name="Resource Categories"
        selectedOptions={filters?.category ?? []}
        onClear={() => updateFilters({ category: undefined })}
      >
        <InputLoader loading={loadingCategories}>
          <SelectableListWithSearch
            selectedOptions={filters?.category ?? []}
            selectableItems={categories}
            onMultiSelect={(category) => updateFilters({ category })}
          />
        </InputLoader>
      </FilterWrapper>
      <FilterWrapper
        name="Resource Types"
        selectedOptions={filters?.typeSubtype ?? []}
        onClear={() => updateFilters({ typeSubtype: undefined })}
      >
        <InputLoader loading={loadingResourceTypes}>
          <SelectableListWithSearch
            selectedOptions={filters?.typeSubtype ?? []}
            selectableItems={resourceTypes}
            onMultiSelect={(typeSubtype) => updateFilters({ typeSubtype })}
          />
        </InputLoader>
      </FilterWrapper>
      <FilterWrapper
        name="Resource Tag Name And Tag Value"
        selectedOptions={filters?.tags ?? []}
        onClear={() =>
          updateFilters({
            tags: undefined,
          })
        }
      >
        <EstateMultiTagsInput
          inputTags={filters.tags}
          placeholderName="Tag"
          onChange={(tags) =>
            updateFilters({
              tags,
            })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Resource Metadata Key And Value"
        selectedOptions={filters?.metadata ?? []}
        onClear={() =>
          updateFilters({
            metadata: undefined,
          })
        }
      >
        <EstateMultiTagsInput
          inputTags={filters.metadata}
          placeholderName="Metadata"
          onChange={(metadata) =>
            updateFilters({
              metadata,
            })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Discovery Time"
        onClear={() =>
          updateFilters({
            firstSeenAtFrom: undefined,
            firstSeenAtTo: undefined,
          })
        }
      >
        <DiscoveryTimeFilter filters={filters} updateFilters={updateFilters} />
      </FilterWrapper>
      <FilterWrapper
        name="Resource Life Span"
        onClear={() =>
          updateFilters({
            lifeSpanFrom: undefined,
            lifeSpanTo: undefined,
          })
        }
      >
        <LifeSpanInput
          filters={filters}
          updateFilters={updateFilters}
          size="m"
        />
      </FilterWrapper>
      <Text weight="medium" marginTop={theme.spacing.spacing04}>
        External Integrations
      </Text>
      <FilterWrapper
        name="External Integrations"
        selectedOptions={filters.externalIntegration ?? []}
        onClear={() => updateFilters({ externalIntegration: undefined })}
      >
        <SelectableListWithSearch
          selectedOptions={filters?.externalIntegration ?? []}
          selectableItems={externalIntegrations}
          onMultiSelect={(externalIntegration) =>
            updateFilters({ externalIntegration })
          }
        />
      </FilterWrapper>
      <Spacer
        height={theme.spacing.spacing04}
        css={{ marginTop: theme.spacing.spacing04 }}
      />
      <FiltersFooter>
        <Button
          disabled={hasEmptyFilters}
          mr={theme.spacing.spacing02}
          onClick={commit}
        >
          Apply filters
        </Button>
        <Button severity="low" disabled={hasEmptyFilters} onClick={reset}>
          Clear filters &amp; reload
        </Button>
      </FiltersFooter>
    </Sidebar>
  );
}
